import { Spinner } from "@/components/ui/spinner";
import { useState } from "react";
import type { RecurringExpenseSuggestionItem } from "@boo/shared";
import { RecurringFrequency } from "@boo/shared";
import { useRecurringExpenseSuggestionStore } from "../store/useRecurringExpenseSuggestionStore";
import EditConfirmModal from "./EditConfirmModal";

const FREQ_LABEL: Record<RecurringFrequency, string> = {
  [RecurringFrequency.WEEKLY]: "Weekly",
  [RecurringFrequency.MONTHLY]: "Monthly",
  [RecurringFrequency.QUARTERLY]: "Quarterly",
  [RecurringFrequency.YEARLY]: "Yearly",
};

function fmt(n: number) {
  return n.toLocaleString("th-TH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

interface Props {
  suggestion: RecurringExpenseSuggestionItem;
}

export default function SuggestionCard({ suggestion }: Props) {
  const { confirmSuggestion, dismissSuggestion } =
    useRecurringExpenseSuggestionStore();
  const [loadingAction, setLoadingAction] = useState<
    "confirm" | "dismiss" | null
  >(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const displayTitle = suggestion.displayName ?? suggestion.merchantName;

  async function handleConfirm() {
    setLoadingAction("confirm");
    await confirmSuggestion(suggestion.id);
    setLoadingAction(null);
  }

  async function handleDismiss() {
    setLoadingAction("dismiss");
    await dismissSuggestion(suggestion.id);
    setLoadingAction(null);
  }

  return (
    <>
      <div className="rounded-xl border border-border bg-card p-4 flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p
              className="text-[13px] font-semibold text-foreground truncate"
              title={displayTitle}
            >
              {displayTitle}
            </p>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="text-[12px] text-muted-foreground">
                {FREQ_LABEL[suggestion.frequency]}
              </span>
              {suggestion.isVariableAmount && (
                <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400">
                  Variable amount
                </span>
              )}
            </div>
          </div>
          <p className="text-[15px] font-bold text-foreground [font-variant-numeric:tabular-nums] flex-shrink-0">
            ฿{fmt(suggestion.averageAmount)}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleConfirm}
            disabled={loadingAction !== null}
            className="flex-1 text-[12px] font-medium py-1.5 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center justify-center"
          >
            {loadingAction === "confirm" ? (
              <Spinner className="size-3.5" />
            ) : (
              "Confirm"
            )}
          </button>
          <button
            onClick={() => setEditModalOpen(true)}
            disabled={loadingAction !== null}
            className="flex-1 text-[12px] font-medium py-1.5 rounded-lg border border-border bg-card text-foreground hover:bg-muted/40 transition-colors disabled:opacity-60"
          >
            Edit &amp; Confirm
          </button>
          <button
            onClick={handleDismiss}
            disabled={loadingAction !== null}
            className="text-[12px] font-medium py-1.5 px-3 rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-60 flex items-center justify-center"
          >
            {loadingAction === "dismiss" ? (
              <Spinner className="size-3.5" />
            ) : (
              "Dismiss"
            )}
          </button>
        </div>
      </div>

      {editModalOpen && (
        <EditConfirmModal
          suggestion={suggestion}
          onClose={() => setEditModalOpen(false)}
        />
      )}
    </>
  );
}
